import type {
  User,
  Company,
  UserGroupInfo,
  CreateUserRequest,
  UpdateUserRequest,
  UserFilter,
  UserListResponse,
  UserQueryParams,
  BulkUserOperation,
  UserStatistics,
  InviteUserRequest,
  ResendInvitationRequest,
  ApiResponse,
} from '@/types/userManagement.types';
import { ApiClient } from '@/config/axios.config';

/**
 * Helper function to build query parameters from UserQueryParams
 */
function buildQueryParams(params?: UserQueryParams): string {
  if (!params) return '';

  const searchParams = new URLSearchParams();

  // Pagination parameters
  if (params.pageNumber !== undefined) {
    searchParams.append('pageNumber', params.pageNumber.toString());
  }
  if (params.pageSize !== undefined) {
    searchParams.append('pageSize', params.pageSize.toString());
  }

  // Sort parameters
  if (params.sortBy) {
    searchParams.append('sortBy', params.sortBy);
  }
  if (params.sortDirection) {
    searchParams.append('sortDirection', params.sortDirection);
  }

  // Filter parameters
  if (params.filter) {
    const { filter } = params;
    if (filter.search) {
      searchParams.append('search', filter.search);
    }
    if (filter.companyId) {
      searchParams.append('companyId', filter.companyId.toString());
    }
    if (filter.userGroupId) {
      searchParams.append('userGroupId', filter.userGroupId.toString());
    }
    if (filter.inviteStatus) {
      searchParams.append('inviteStatus', filter.inviteStatus);
    }
    if (filter.notificationEnabled !== undefined) {
      searchParams.append('notificationEnabled', filter.notificationEnabled.toString());
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * UserManagementService - handles all API calls related to user management
 */
export class UserManagementService {
  /**
   * Get paginated list of users with optional filtering and sorting
   */
  static async getUsers(params?: UserQueryParams): Promise<UserListResponse> {
    const queryString = buildQueryParams(params);
    // API returns an array directly, so we need to wrap it in UserListResponse format
    const users = await ApiClient.get<User[]>(`/users${queryString}`);

    // Since the API doesn't provide pagination info, we'll simulate it
    return {
      users,
      totalCount: users.length,
      totalPages: 1,
      pageNumber: params?.pageNumber || 1,
      pageSize: params?.pageSize || 10,
    };
  }

  /**
   * Get a single user by ID
   */
  static async getUserById(userId: number): Promise<User> {
    return ApiClient.get<User>(`/users/${userId}`);
  }

  /**
   * Create a new user
   */
  static async createUser(data: CreateUserRequest): Promise<User> {
    return ApiClient.post<User>('/users', data);
  }

  /**
   * Update an existing user
   */
  static async updateUser(userId: number, data: UpdateUserRequest): Promise<User> {
    return ApiClient.put<User>(`/users/${userId}`, data);
  }

  /**
   * Delete a user
   */
  static async deleteUser(userId: number): Promise<void> {
    await ApiClient.delete<void>(`/users/${userId}`);
  }

  /**
   * Invite a new user
   */
  static async inviteUser(data: InviteUserRequest): Promise<User> {
    return ApiClient.post<User>('/users/invite', data);
  }

  /**
   * Resend invitation to a user
   */
  static async resendInvitation(data: ResendInvitationRequest): Promise<void> {
    await ApiClient.post<void>(`/users/invite/${data.userId}`, {});
  }

  /**
   * Bulk operations on multiple users
   */
  static async bulkOperation(data: BulkUserOperation): Promise<void> {
    await ApiClient.post<void>('/users/bulk', data);
  }

  /**
   * Get user statistics
   */
  static async getUserStatistics(): Promise<UserStatistics> {
    return ApiClient.get<UserStatistics>('/users/statistics');
  }

  /**
   * Get all companies for user assignment
   */
  static async getCompanies(): Promise<Company[]> {
    const companies = await ApiClient.get<Company[]>('/companies');
    // Sort by name for consistent display
    return companies.sort((a: Company, b: Company) => a.name.localeCompare(b.name));
  }

  /**
   * Get all user groups for user assignment
   * This reuses the existing UserGroupService endpoint but returns minimal data
   */
  static async getUserGroups(): Promise<UserGroupInfo[]> {
    const userGroups = await ApiClient.get<UserGroupInfo[]>('/usergroups?minimal=true');
    return userGroups.sort((a: UserGroupInfo, b: UserGroupInfo) => a.name.localeCompare(b.name));
  }

  /**
   * Search users with autocomplete functionality
   */
  static async searchUsers(query: string, limit = 10): Promise<User[]> {
    return ApiClient.get<User[]>(`/users/search?q=${encodeURIComponent(query)}&limit=${limit}`);
  }

  /**
   * Export users to CSV
   */
  static async exportUsers(filter?: UserFilter): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (filter) {
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const queryString = queryParams.toString();
    return ApiClient.download(`/users/export${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Import users from CSV
   */
  static async importUsers(
    file: File
  ): Promise<ApiResponse<{ imported: number; errors: string[] }>> {
    const formData = new FormData();
    formData.append('file', file);

    // Use ApiClient but with FormData (it will handle Content-Type automatically)
    return ApiClient.post<ApiResponse<{ imported: number; errors: string[] }>>(
      '/users/import',
      formData
    );
  }

  /**
   * Validate email address before creating user
   */
  static async validateEmail(email: string): Promise<{ isValid: boolean; message?: string }> {
    return ApiClient.post<{ isValid: boolean; message?: string }>('/users/validate-email', {
      email,
    });
  }

  /**
   * Get user activity log
   */
  static async getUserActivity(
    userId: number,
    limit = 50
  ): Promise<Array<{ timestamp: string; action: string; details: string }>> {
    return ApiClient.get<Array<{ timestamp: string; action: string; details: string }>>(
      `/users/${userId}/activity?limit=${limit}`
    );
  }
}
